import { Form, Formik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, MouseEventHandler, useState } from 'react';
import Ripples from 'react-ripples';
import * as yup from 'yup';
import Link from 'next/link';
import alphanumeric from '../../../helpers/alphanumeric';
import { AddressDeliveryType } from '../../../models/address-delivery-type';
import { CountryType } from '../../../models/country-type';
import { LoqateAddressRetrieveType } from '../../../models/loqate-address-retrieve-type';
import { UpdateDeliveryAddressesType } from '../../../models/update-delivery-addresses-type';
import FormErrorTypeEnum from '../../../models/form-error-type-enum';
import addressCreate from '../../../services/customer-api/endpoints/address/create';
import addressUpdate from '../../../services/customer-api/endpoints/address/update';
import ButtonPrimary from '../../atoms/button-primary/button-primary';
import ButtonSecondary from '../../atoms/button-secondary/button-secondary';
import FormSelect from '../../atoms/form-select/form-select';
import FormText from '../../atoms/form-text/form-text';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import useGetSessionId from '../../providers/user/hooks/use-get-session-id.hook';
import AddressLookup from '../address-lookup/address-lookup';
import AddressResponseCode from '../../../models/address-response-code-enum';
import ValidationException from '../../../services/exceptions/validation-exception';
import usePushFormikFormToDataLayer from '../../../hooks/use-push-formik-form-to-data-layer/use-push-formik-form-to-data-layer';
import getTailwindConfig from '../../../helpers/get-tailwind-config';

interface Props extends UpdateDeliveryAddressesType {
  readonly address: AddressDeliveryType;
  readonly countries: CountryType[];
  readonly fullWidth?: boolean;
  readonly onCancel?: MouseEventHandler<HTMLButtonElement>;
  readonly onDelete?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  readonly onSubmit?: (address: AddressDeliveryType) => void;
  readonly postCreate?: boolean;
  readonly postUpdate?: boolean;
}

const UpdateAddressForm: FC<Props> = ({
  addAddressButtonText,
  addNewAddressHeading,
  address,
  addressLine1FieldLabel,
  addressLine1FieldMandatoryError,
  addressLine1PlaceholderLabel,
  addressLine2FieldLabel,
  addressLine2PlaceholderLabel,
  addressLine3FieldLabel,
  addressLine3PlaceholderLabel,
  addressLine4FieldLabel,
  addressLine4PlaceholderLabel,
  addressLine5FieldLabel,
  addressLine5PlaceholderLabel,
  addressNameFieldLabel,
  addressNameFieldMandatoryError,
  addressNamePlaceholderLabel,
  countries,
  countryFieldLabel,
  countryFieldMandatoryError,
  countryPlaceholderLabel,
  deleteAddressButtonText,
  editAddressHeading,
  fullWidth,
  goBackLabelText,
  invalidPostcodeError,
  lookupAddressFieldLabel,
  lookupAddressManualButtonText,
  lookupAddressPlaceholder,
  onCancel,
  onDelete,
  onSubmit,
  postCreate,
  postCodeFieldLabel,
  postCodeFieldMandatoryError,
  postCodePlaceholderLabel,
  postUpdate,
  updateAddressButtonText,
}: Props) => {
  const [showAddressForm, setShowAddressForm] = useState<boolean>(false);
  const getSessionId = useGetSessionId();
  const fullConfig = getTailwindConfig();
  const frenchBlue = fullConfig.theme.colors['french-blue'];
  const { alphanumericFormat, alphanumericValidationMessage } = alphanumeric();
  const [formSuccessfullySubmitted, setFormSuccessfullySubmitted] =
    useState<boolean>(null);

  const formContext = usePushFormikFormToDataLayer<AddressDeliveryType>(
    'Update address',
    formSuccessfullySubmitted
  );

  return (
    <div
      className={
        `${fullWidth ? '' : 'md:w-8/12 lg:w-6/12 xl:w-4/12 '}` +
        'mx-auto w-full'
      }
    >
      {onCancel && (
        <div className="flex justify-center mb-2 md:mb-4 lg:mb-6">
          <Ripples
            className="rounded-full"
            color={`rgba(${frenchBlue.rgb}, .2)`}
            during={1200}
          >
            <button
              className="flex items-center focus:outline-none px-4 py-2 rounded-full text-grey-600 text-left"
              onClick={onCancel}
              type="button"
            >
              <IcomoonIcon
                className="-mb-2px"
                color="grey-600"
                name="chevron-left"
                size="md"
              />

              <span className="ml-4">{goBackLabelText}</span>
            </button>
          </Ripples>
        </div>
      )}

      <h4 className="page-heading-four text-center mb-8 md:mb-10 lg:mb-12">
        {address ? editAddressHeading : addNewAddressHeading}
      </h4>

      <Formik
        initialValues={{
          name: address?.name || '',
          addressLine1: address?.addressLine1 || '',
          addressLine2: address?.addressLine2 || '',
          addressLine3: address?.addressLine3 || '',
          addressLine4: address?.addressLine4 || '',
          addressLine5: address?.addressLine5 || '',
          countryCode: address?.countryCode || '',
          postcode: address?.postcode || '',
        }}
        validationSchema={yup.object({
          name: yup
            .string()
            .max(35)
            .matches(alphanumericFormat, alphanumericValidationMessage)
            .required(addressNameFieldMandatoryError)
            .trim(addressNameFieldMandatoryError)
            .label(addressNameFieldLabel),
          addressLine1: yup
            .string()
            .max(35)
            .matches(alphanumericFormat, alphanumericValidationMessage)
            .required(addressLine1FieldMandatoryError)
            .trim(addressLine1FieldMandatoryError)
            .label(addressLine1FieldLabel),
          addressLine2: yup
            .string()
            .max(35)
            .matches(alphanumericFormat, alphanumericValidationMessage)
            .label(addressLine2FieldLabel),
          addressLine3: yup
            .string()
            .max(35)
            .matches(alphanumericFormat, alphanumericValidationMessage)
            .label(addressLine3FieldLabel),
          addressLine4: yup
            .string()
            .max(35)
            .matches(alphanumericFormat, alphanumericValidationMessage)
            .label(addressLine4FieldLabel),
          addressLine5: yup
            .string()
            .max(35)
            .matches(alphanumericFormat, alphanumericValidationMessage)
            .label(addressLine5FieldLabel),
          postcode: yup
            .string()
            .when('countryCode', {
              is: 'GBR',
              then: yup
                .string()
                .max(10)
                .matches(alphanumericFormat, alphanumericValidationMessage)
                .required(postCodeFieldMandatoryError)
                .trim(postCodeFieldMandatoryError),
            })
            .label(postCodeFieldLabel),
          countryCode: yup
            .string()
            .max(3)
            .matches(alphanumericFormat, alphanumericValidationMessage)
            .required(countryFieldMandatoryError)
            .label(countryFieldLabel),
        })}
        onSubmit={async (values, { setErrors }) => {
          try {
            let response: AddressResponseCode;

            if (address && postUpdate) {
              response = await addressUpdate(
                { ...values, id: address.id },
                await getSessionId()
              );
            } else if (postCreate) {
              response = await addressCreate(values, await getSessionId());
            }

            if (
              response === AddressResponseCode.InvalidPostcode1 ||
              response === AddressResponseCode.InvalidPostcode2
            ) {
              throw new ValidationException({ postcode: invalidPostcodeError });
            }

            onSubmit({ ...values, id: address?.id });

            setFormSuccessfullySubmitted(true);
          } catch (exception) {
            if (exception instanceof ValidationException) {
              setErrors(exception.messages);

              return;
            }

            setFormSuccessfullySubmitted(false);
          }
        }}
        innerRef={formContext}
      >
        {({ setFieldValue, submitForm, values }) => {
          const formHasChanged =
            values.name !== address?.name ||
            values.addressLine1 !== address?.addressLine1 ||
            values.addressLine2 !== address?.addressLine2 ||
            values.addressLine3 !== address?.addressLine3 ||
            values.addressLine4 !== address?.addressLine4 ||
            values.addressLine5 !== address?.addressLine5 ||
            values.postcode !== address?.postcode ||
            values.countryCode !== address?.countryCode;

          return (
            <Form className="flex flex-col space-y-5">
              <FormSelect
                disabled={!countries?.length}
                label={countryFieldLabel}
                name="countryCode"
                onChange={() => setFieldValue('postcode', '')}
                options={countries.map((country: CountryType) => ({
                  label: country.name,
                  value: country.iso3Code,
                }))}
                placeholder={countryPlaceholderLabel}
                required
              />

              <AnimatePresence>
                {values.countryCode && (
                  <motion.div
                    animate={{ height: 'auto', opacity: 1 }}
                    className="flex flex-col space-y-5"
                    initial={{ height: 0, opacity: 0 }}
                  >
                    <AddressLookup
                      countryCode={values.countryCode}
                      ctaLabel={lookupAddressManualButtonText}
                      label={lookupAddressFieldLabel}
                      onEnterManually={
                        !address && (() => setShowAddressForm(true))
                      }
                      onSelect={(
                        selectedAddress: LoqateAddressRetrieveType
                      ) => {
                        setFieldValue('name', selectedAddress.Company);
                        setFieldValue('companyName', selectedAddress.Company);
                        setFieldValue('addressLine1', selectedAddress.Line1);
                        setFieldValue('addressLine2', selectedAddress.Line2);
                        setFieldValue('addressLine3', selectedAddress.Line3);
                        setFieldValue('city', selectedAddress.City);
                        setFieldValue('county', selectedAddress.Province);
                        setFieldValue('country', selectedAddress.CountryIso3);
                        setFieldValue(
                          'postcode',
                          selectedAddress.CountryIso3 === 'GBR'
                            ? selectedAddress.PostalCode
                            : ''
                        );
                        setShowAddressForm(true);
                      }}
                      placeholder={lookupAddressPlaceholder}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {(showAddressForm || address) && (
                  <motion.div
                    animate={{ height: 'auto', opacity: 1 }}
                    className="flex flex-col space-y-5"
                    initial={{ height: 0, opacity: 0 }}
                  >
                    <hr className="border-grey-100 my-5" />

                    <FormText
                      label={addressNameFieldLabel}
                      name="name"
                      placeholder={addressNamePlaceholderLabel}
                      required
                    />

                    <FormText
                      label={addressLine1FieldLabel}
                      name="addressLine1"
                      placeholder={addressLine1PlaceholderLabel}
                      required
                    />

                    <FormText
                      label={addressLine2FieldLabel}
                      name="addressLine2"
                      placeholder={addressLine2PlaceholderLabel}
                    />

                    <FormText
                      label={addressLine3FieldLabel}
                      name="addressLine3"
                      placeholder={addressLine3PlaceholderLabel}
                    />

                    <FormText
                      label={addressLine4FieldLabel}
                      name="addressLine4"
                      placeholder={addressLine4PlaceholderLabel}
                    />

                    <FormText
                      label={addressLine5FieldLabel}
                      name="addressLine5"
                      placeholder={addressLine5PlaceholderLabel}
                    />

                    {values.countryCode === 'GBR' && (
                      <FormText
                        label={postCodeFieldLabel}
                        name="postcode"
                        placeholder={postCodePlaceholderLabel}
                        required
                      />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <p className="text-center">
                For deliveries outside of these areas please contact{' '}
                <Link href="mailto:sales@flowtech.co.uk" passHref>
                  <a
                    href="/"
                    className="text-french-blue hover:text-french-blue-light transition-colors duration-400 ease-out cursor-pointer"
                  >
                    sales@flowtech.co.uk
                  </a>
                </Link>
              </p>

              {formSuccessfullySubmitted === false && (
                <span className="block mt-1 text-sm text-invalid">
                  {FormErrorTypeEnum.Unknown}
                </span>
              )}

              <div
                className={`${onDelete ? 'md:-mx-20' : ''
                  } flex flex-col-reverse md:flex-row md:space-x-4 justify-center py-component-spacing-mobile lg:py-component-spacing-content`}
              >
                {onDelete && (
                  <div className="pt-6 md:pt-0">
                    <ButtonSecondary
                      name={deleteAddressButtonText}
                      onClick={(event) => onDelete(event)}
                    />
                  </div>
                )}

                <ButtonPrimary
                  disabled={
                    !formHasChanged ||
                    !values.countryCode ||
                    !(showAddressForm || address)
                  }
                  name={
                    address ? updateAddressButtonText : addAddressButtonText
                  }
                  onClick={() => submitForm()}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

UpdateAddressForm.defaultProps = {
  fullWidth: false,
  onCancel: null,
  onDelete: null,
  onSubmit: () => ({}),
  postCreate: true,
  postUpdate: true,
};

export default UpdateAddressForm;
